import {
  contactsToCsv,
  type Contact,
  type ContactConsentStatus,
  type ContactWithVenue,
} from '@sixam/shared/contact-core';
import { getApiUrl } from '@/lib/api-url';
import { authedFetch } from '@/lib/authed-fetch';
import { DEFAULT_TENANT_SLUG } from '@/lib/tenant';

export type ContactCapturePayload = Readonly<{
  tenantId?: string;
  tenantSlug?: string;
  eventId?: string;
  venueId?: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  consentStatus?: ContactConsentStatus;
  source: string;
}>;

async function parseApiResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
  const payload = (await response.json().catch(() => null)) as ({ error?: string } & T) | null;

  if (!response.ok) {
    throw new Error(payload?.error ?? fallbackMessage);
  }

  return payload as T;
}

export async function captureContact(
  payload: ContactCapturePayload,
  options: { authenticated?: boolean } = {}
): Promise<{ status: 'ok' }> {
  const request = {
    body: JSON.stringify({
      tenantSlug: DEFAULT_TENANT_SLUG,
      ...payload,
    }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  };

  const response = options.authenticated
    ? await authedFetch('/api/contacts', request)
    : await fetch(getApiUrl('/api/contacts'), request);

  return parseApiResponse<{ status: 'ok' }>(response, 'Could not save contact.');
}

export async function fetchMyContact(tenantId: string): Promise<Contact | null> {
  const response = await authedFetch(`/api/contacts?tenantId=${encodeURIComponent(tenantId)}`);
  return parseApiResponse<Contact | null>(response, 'Could not load contact.');
}

export async function fetchAdminContacts(tenantSlug: string): Promise<ContactWithVenue[]> {
  const response = await authedFetch(`/api/admin/${encodeURIComponent(tenantSlug)}/contacts`);
  return parseApiResponse<ContactWithVenue[]>(response, 'Could not load contacts.');
}

export async function exportAdminContactsCsv(tenantSlug: string): Promise<string> {
  const response = await authedFetch(
    `/api/admin/${encodeURIComponent(tenantSlug)}/contacts?format=csv`
  );
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? 'Could not export contacts.');
  }
  return response.text();
}

export function buildContactsCsvPreview(contacts: Contact[]): string {
  return contactsToCsv(contacts);
}
